import { css, html, LitElement } from 'lit';
import type { CountdownTimer, RpsThrow, RpsThrowOrTimeout } from '@/shared/messages.ts';

type RpsResult = {
  count: number;
  rpsA: RpsThrowOrTimeout;
  rpsB: RpsThrowOrTimeout;
  winnerId?: number;
};

export class BizTiebreaker extends LitElement {
  static properties = {
    topic: { type: String },
    roundLabel: { type: String, attribute: 'round-label' },
    responseAId: { type: Number, attribute: 'response-a-id' },
    responseAText: { type: String, attribute: 'response-a-text' },
    responseBId: { type: Number, attribute: 'response-b-id' },
    responseBText: { type: String, attribute: 'response-b-text' },
    userIdA: { type: String, attribute: 'user-id-a' },
    userIdB: { type: String, attribute: 'user-id-b' },
    nameA: { type: String, attribute: 'name-a' },
    nameB: { type: String, attribute: 'name-b' },
    selfUserId: { type: String, attribute: 'self-user-id' },
    count: { type: Number },
    timer: { attribute: false },
    isHost: { type: Boolean, attribute: 'is-host' },
    myThrow: { type: String, attribute: 'my-throw' },
    result: { attribute: false },
    _remaining: { state: true },
  };

  declare topic: string;
  declare roundLabel: string;
  declare responseAId: number;
  declare responseAText: string;
  declare responseBId: number;
  declare responseBText: string;
  declare userIdA: string;
  declare userIdB: string;
  declare nameA: string;
  declare nameB: string;
  declare selfUserId: string;
  declare count: number;
  declare timer: CountdownTimer | null;
  declare isHost: boolean;
  declare myThrow: RpsThrow | '';
  declare result: RpsResult | null;
  declare _remaining: number;

  #tickHandle: number | null = null;

  constructor() {
    super();
    this.topic = '';
    this.roundLabel = '';
    this.responseAId = 0;
    this.responseAText = '';
    this.responseBId = 0;
    this.responseBText = '';
    this.userIdA = '';
    this.userIdB = '';
    this.nameA = '';
    this.nameB = '';
    this.selfUserId = '';
    this.count = 1;
    this.timer = null;
    this.isHost = false;
    this.myThrow = '';
    this.result = null;
    this._remaining = 0;
  }

  static styles = css`
    :host {
      display: block;
      max-width: 36rem;
      margin: 2rem auto;
      padding: 0 1rem;
    }
    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    .topic-block {
      display: flex;
      flex-direction: column;
    }
    .topic {
      font-size: 1.1rem;
    }
    .round-label {
      color: var(--fg-dim, #a0a4ad);
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-top: 0.15rem;
    }
    .timer-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .timer {
      font-variant-numeric: tabular-nums;
      font-size: 1.2rem;
      color: var(--fg-dim, #a0a4ad);
    }
    .timer.paused {
      color: var(--accent, #5fbf73);
    }
    .pause-btn {
      background: transparent;
      border: 1px solid var(--border, #2c2e36);
      color: var(--fg, #ececec);
      padding: 0.15rem 0.5rem;
      border-radius: 4px;
      font: inherit;
      line-height: 1;
      cursor: pointer;
    }
    .banner {
      text-align: center;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--accent, #5fbf73);
      margin-bottom: 0.75rem;
    }
    .responses {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }
    .card {
      background: var(--bg-card, #1f2128);
      border: 1px solid var(--border, #2c2e36);
      border-radius: 8px;
      padding: 1rem;
      text-align: center;
    }
    .card .name {
      color: var(--fg-dim, #a0a4ad);
      font-size: 0.85rem;
      margin-top: 0.4rem;
    }
    .card.winner {
      border-color: var(--accent, #5fbf73);
      background: rgba(95, 191, 115, 0.08);
    }
    .rps {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }
    .rps button {
      font-size: 1.6rem;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      border: 1px solid var(--border, #2c2e36);
      background: var(--bg-card, #1f2128);
      color: var(--fg, #ececec);
      cursor: pointer;
    }
    .rps button:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    .rps button.chosen {
      border-color: var(--accent, #5fbf73);
      background: rgba(95, 191, 115, 0.12);
    }
    .hint {
      color: var(--fg-dim, #a0a4ad);
      font-style: italic;
      text-align: center;
    }
    .throws {
      display: flex;
      justify-content: space-around;
      font-size: 2rem;
      margin: 0.5rem 0;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.#startTicker();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopTicker();
  }

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('timer')) {
      this.#stopTicker();
      this.#startTicker();
    }
  }

  render() {
    const paused = !!this.timer?.paused;
    const result = this.result;
    const amThrower = this.selfUserId === this.userIdA || this.selfUserId === this.userIdB;
    const aWon = result?.winnerId === this.responseAId;
    const bWon = result?.winnerId === this.responseBId;

    return html`
      <link rel="stylesheet" href="/static/style.css" />
      <header>
        <span class="topic-block">
          <span class="topic">${this.topic}</span>
          ${this.roundLabel
            ? html`<span class="round-label">${this.roundLabel}</span>`
            : null}
        </span>
        ${result
          ? null
          : html`
            <span class="timer-group">
              <span class="timer ${paused ? 'paused' : ''}">${this._remaining}s</span>
              ${this.isHost
                ? html`
                  <button
                    class="pause-btn"
                    title="${paused ? 'Resume' : 'Pause'}"
                    @click=${() => this.#onTogglePause(paused)}
                  >
                    ${paused ? '&#9654;' : '&#9208;'}
                  </button>
                `
                : null}
            </span>
          `}
      </header>

      <div class="banner">
        ${this.count > 1
          ? html`Tiebreaker&mdash;round ${this.count}`
          : 'Tiebreaker'}
      </div>

      <div class="responses">
        <div class="card ${aWon ? 'winner' : ''}">
          <div>${this.responseAText}</div>
          <div class="name">&mdash;${this.nameA || 'Player A'}</div>
        </div>
        <div class="card ${bWon ? 'winner' : ''}">
          <div>${this.responseBText}</div>
          <div class="name">&mdash;${this.nameB || 'Player B'}</div>
        </div>
      </div>

      ${result ? this.#renderResult(result) : this.#renderPrompt(amThrower)}
    `;
  }

  #renderPrompt(amThrower: boolean) {
    if (!amThrower) {
      return html`
        <p class="hint">
          ${this.nameA || 'Player A'} and ${this.nameB || 'Player B'} are throwing&hellip;
        </p>
      `;
    }
    if (this.myThrow) {
      return html`<p class="hint">Throw locked in. Waiting on opponent&hellip;</p>`;
    }
    const choices: Array<{ value: RpsThrow; glyph: string }> = [
      { value: 'rock', glyph: '\u{270A}' },
      { value: 'paper', glyph: '\u{270B}' },
      { value: 'scissors', glyph: '\u{270C}' },
    ];
    return html`
      <div class="rps">
        ${choices.map(
          (c) =>
            html`
              <button
                class="${this.myThrow === c.value ? 'chosen' : ''}"
                @click=${() => this.#onThrow(c.value)}
              >${c.glyph}</button>
            `,
        )}
      </div>
    `;
  }

  #renderResult(result: RpsResult) {
    return html`
      <div class="throws">
        <span>${this.#glyphFor(result.rpsA)}</span>
        <span>vs</span>
        <span>${this.#glyphFor(result.rpsB)}</span>
      </div>
      <p class="hint">
        ${result.winnerId
          ? html`Winner: ${aIs(result.winnerId, this.responseAId) ? this.nameA : this.nameB}`
          : html`Draw&mdash;throwing again!`}
      </p>
    `;
  }

  #glyphFor(t: RpsThrowOrTimeout): string {
    if (t === 'rock') return '\u{270A}';
    if (t === 'paper') return '\u{270B}';
    if (t === 'scissors') return '\u{270C}';
    return '\u{23F1}';
  }

  #onThrow(t: RpsThrow) {
    this.dispatchEvent(
      new CustomEvent('biz-break-tie', {
        detail: { throw: t },
        bubbles: true,
        composed: true,
      }),
    );
  }

  #onTogglePause(currentlyPaused: boolean) {
    this.dispatchEvent(
      new CustomEvent(currentlyPaused ? 'biz-unpause-game' : 'biz-pause-game', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  #startTicker() {
    if (!this.timer) return;
    const anchor = performance.now();
    const totalMs = this.timer.ms;
    if (this.timer.paused) {
      this._remaining = Math.max(0, Math.ceil(totalMs / 1000));
      return;
    }
    const tick = () => {
      if (!this.timer) {
        this.#stopTicker();
        return;
      }
      const elapsedMs = performance.now() - anchor;
      this._remaining = Math.max(0, Math.ceil((totalMs - elapsedMs) / 1000));
    };
    tick();
    this.#tickHandle = setInterval(tick, 250) as unknown as number;
  }

  #stopTicker() {
    if (this.#tickHandle !== null) {
      clearInterval(this.#tickHandle);
      this.#tickHandle = null;
    }
  }
}

function aIs(winnerId: number, responseAId: number): boolean {
  return winnerId === responseAId;
}

customElements.define('biz-tiebreaker', BizTiebreaker);
